import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { LoginCredentials } from '../types'
import type { Database } from '@/shared/types/database'
import type { Session, User } from '@supabase/supabase-js'

type UserRole = Database['public']['Enums']['user_role']

export interface UserProfile {
  id: string
  email: string
  first_name: string | null
  last_name: string | null
  phone: string | null
  manager_id: string | null
  agency_id: string | null
  invitation_status: 'pending' | 'active' | 'expired' | null
}

export interface UserResidence {
  id: string
  residence_id: string
  role: UserRole
  residences: {
    id: string
    name: string
    residence_key: string | null
  } | null
}

export interface AuthData {
  session: Session | null
  user: User | null
  profile: UserProfile | null
  residences: UserResidence[]
  isAdmin: boolean
  isManager: (residenceId: string) => boolean
  isAccountant: (residenceId: string) => boolean
  isOwner: boolean
  hasResidenceAccess: (residenceId: string) => boolean
  getUserRole: (residenceId: string) => UserRole | null
}

/**
 * Enhanced auth hook that loads user profile and residence assignments with roles
 */
export function useAuth() {
  return useQuery({
    queryKey: ['auth', 'user'],
    queryFn: async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (!session) return null

      // Fetch user profile from users table
      const { data: profile, error: profileError } = await supabase
        .from('users')
        .select('id, email, first_name, last_name, phone, manager_id, agency_id, invitation_status')
        .eq('id', session.user.id)
        .single()

      if (profileError) {
        console.error('Error fetching user profile:', profileError)
        // Continue without profile if it doesn't exist yet
      }

      // Fetch user's own residences (simple query without joins to avoid RLS recursion)
      const { data: userResidenceRows, error: residencesError } = await supabase
        .from('user_residences')
        .select('id, residence_id, role')
        .eq('user_id', session.user.id)

      if (residencesError) {
        console.error('Error fetching user residences:', residencesError)
      }

      // Fetch residence details separately
      let userResidences: UserResidence[] = []
      if (userResidenceRows && userResidenceRows.length > 0) {
        console.log('🔍 [useAuth] Found user_residences:', userResidenceRows.length)
        const residenceIds = userResidenceRows.map((ur) => ur.residence_id)
        console.log('🔍 [useAuth] Residence IDs to fetch:', residenceIds)

        const { data: residenceData, error: residenceError } = await supabase
          .from('residences')
          .select('id, name, residence_key')
          .in('id', residenceIds)

        if (residenceError) {
          console.error('❌ [useAuth] Error fetching residences:', residenceError)
        } else {
          console.log('✅ [useAuth] Fetched residences:', residenceData?.length)
        }

        // Merge the data
        userResidences = userResidenceRows.map((ur) => ({
          ...ur,
          residences: residenceData?.find((r) => r.id === ur.residence_id) || null,
        })) as UserResidence[]

        console.log('🔍 [useAuth] Final userResidences:', userResidences)
      } else {
        console.log('⚠️ [useAuth] No user_residences found for user')
        userResidences = []
      }

      // Helper functions for role checking
      const isAdmin = userResidences.some((r) => r.role === 'ADMIN')

      const isManager = (residenceId: string) => {
        return userResidences.some(
          (r) =>
            r.residence_id === residenceId && (r.role === 'ADMIN' || r.role === 'MANAGER')
        )
      }

      const isAccountant = (residenceId: string) => {
        return userResidences.some(
          (r) => r.residence_id === residenceId && r.role === 'ACCOUNTANT'
        )
      }

      const isOwner = userResidences.some((r) => r.role === 'OWNER')

      const hasResidenceAccess = (residenceId: string) => {
        return userResidences.some((r) => r.residence_id === residenceId)
      }

      const getUserRole = (residenceId: string): UserRole | null => {
        const residence = userResidences.find((r) => r.residence_id === residenceId)
        return residence?.role || null
      }

      const authData: AuthData = {
        session,
        user: session.user,
        profile: profile ? {
          ...profile,
          invitation_status: profile.invitation_status as 'pending' | 'active' | 'expired' | null
        } : null,
        residences: userResidences,
        isAdmin,
        isManager,
        isAccountant,
        isOwner,
        hasResidenceAccess,
        getUserRole,
      }

      return authData
    },
  })
}

export function useLogin() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (credentials: LoginCredentials) => {
      const { data, error } = await supabase.auth.signInWithPassword(credentials)

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auth'] })
    },
  })
}

export function useLogout() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
    },
    onSuccess: () => {
      // Clear ALL cached data when logging out to prevent stale data
      // when logging in as a different user or with a different agency
      queryClient.clear()
    },
  })
}
